import { useState, useEffect, useRef, useCallback } from 'react'
import { Bell, Check, MessageSquare, Handshake, X, ChevronRight } from 'lucide-react'
import { chatApi } from '../lib/chatApi'
import { useAuth } from '../hooks/useAuth'

function timeAgo(iso) {
  if (!iso) return ''
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
}

function iconForType(type) {
  if (type === 'chat_message') return MessageSquare
  if (type === 'agent_request') return Handshake
  return Bell
}

export default function NotificationsCenter({ onNavigate }) {
  const { user } = useAuth()
  const userId = user?.id
  const [notifications, setNotifications] = useState([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const dropdownRef = useRef(null)

  const loadNotifications = useCallback(async () => {
    if (!userId) { setLoading(false); return }
    const { data } = await chatApi.getNotifications(userId)
    setNotifications(data || [])
    setLoading(false)
  }, [userId])

  useEffect(() => {
    loadNotifications()
  }, [loadNotifications])

  // Realtime subscription
  useEffect(() => {
    if (!userId) return
    let unsub = () => {}
    chatApi.subscribeToNotifications(userId, (newNotif) => {
      setNotifications((prev) => [newNotif, ...prev])
    }).then((fn) => { unsub = fn })
    return () => unsub()
  }, [userId])

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const unreadCount = notifications.filter((n) => !n.read).length

  const handleClick = async (notif) => {
    if (!notif.read) {
      await chatApi.markNotificationRead(notif.id)
      setNotifications((prev) =>
        prev.map((n) => (n.id === notif.id ? { ...n, read: true } : n))
      )
    }
    setOpen(false)
    if (notif.type === 'chat_message' && notif.data?.conversation_id) {
      onNavigate?.('messages')
    } else if (notif.type === 'agent_request') {
      onNavigate?.('agent-requests')
    }
  }

  const handleMarkAll = async () => {
    if (!userId || unreadCount === 0) return
    await chatApi.markAllNotificationsRead(userId)
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen((p) => !p)}
        className="relative p-2 text-neutral-500 hover:text-ink transition-colors focus:outline-none focus:ring-2 focus:ring-brand"
        aria-label="Notifications"
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-surface border border-neutral-200 shadow-lg z-50 animate-fade-in">
          <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200">
            <h3 className="text-sm font-semibold text-ink">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAll}
                  className="text-[11px] text-brand hover:text-brand-dark font-semibold flex items-center gap-1"
                >
                  <Check size={12} /> Mark all read
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="text-neutral-400 hover:text-ink"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {loading && (
              <div className="px-4 py-8 text-center text-xs text-muted">Loading...</div>
            )}
            {!loading && notifications.length === 0 && (
              <div className="px-4 py-10 text-center">
                <Bell size={24} className="text-neutral-300 mx-auto mb-3" />
                <p className="text-sm font-semibold text-ink">No notifications</p>
                <p className="text-xs text-muted mt-1">You'll see updates here when agents respond or new messages arrive.</p>
              </div>
            )}
            {!loading && notifications.map((notif) => {
              const Icon = iconForType(notif.type)
              return (
                <button
                  key={notif.id}
                  onClick={() => handleClick(notif)}
                  className={`w-full px-4 py-3 flex items-start gap-3 text-left border-b border-neutral-100 hover:bg-neutral-50 transition-colors ${!notif.read ? 'bg-brand-soft/30' : ''}`}
                >
                  <div className={`w-8 h-8 shrink-0 flex items-center justify-center ${notif.type === 'chat_message' ? 'bg-brand-soft' : 'bg-amber-50'}`}>
                    <Icon size={14} className={notif.type === 'chat_message' ? 'text-brand' : 'text-amber-600'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-ink leading-tight">{notif.title}</p>
                    <p className="text-xs text-muted mt-0.5 line-clamp-2">{notif.message}</p>
                    <span className="text-[10px] text-neutral-400 mt-1 block">{timeAgo(notif.created_at)}</span>
                  </div>
                  {!notif.read && <span className="w-2 h-2 bg-rose-500 rounded-full shrink-0 mt-1.5" />}
                  <ChevronRight size={14} className="text-neutral-300 shrink-0 mt-1.5" />
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
